// frontend/src/pages/partner/ServicesPartner.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import './ServicesPartner.css';

const ICON_COLORS = [
  { bg: 'linear-gradient(135deg, #2563eb, #0891b2)', shadow: 'rgba(37,99,235,0.30)' },
  { bg: 'linear-gradient(135deg, #0891b2, #06b6d4)', shadow: 'rgba(8,145,178,0.30)' },
  { bg: 'linear-gradient(135deg, #4f46e5, #2563eb)', shadow: 'rgba(79,70,229,0.30)' },
  { bg: 'linear-gradient(135deg, #1d4ed8, #0e7490)', shadow: 'rgba(29,78,216,0.30)' },
  { bg: 'linear-gradient(135deg, #0369a1, #0284c7)', shadow: 'rgba(3,105,161,0.30)' },
  { bg: 'linear-gradient(135deg, #3b82f6, #6366f1)', shadow: 'rgba(59,130,246,0.30)' },
  { bg: 'linear-gradient(135deg, #0e7490, #0891b2)', shadow: 'rgba(14,116,144,0.30)' },
  { bg: 'linear-gradient(135deg, #1e40af, #2563eb)', shadow: 'rgba(30,64,175,0.30)' },
];

const getIconColor = (name) => ICON_COLORS[(name?.charCodeAt(0) || 0) % ICON_COLORS.length];

const CATEGORIES = [
  { id: 'all',      label: 'Tous les services', icon: 'fas fa-th-large' },
  { id: 'active',   label: 'Services actifs',   icon: 'fas fa-check-circle' },
  { id: 'inactive', label: 'Services inactifs', icon: 'fas fa-pause-circle' },
];

// ── Service Card ──────────────────────────────────────────────
const ServiceCard = ({ service, stats, onReplenish, onDelete, navigate, viewMode }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const color = getIconColor(service.name);
  const isActive = (stats?.points || 0) >= 5000;
  const pts = stats?.points || 0;

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (viewMode === 'list') {
    return (
      <motion.div
        className="sp-list-row"
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ backgroundColor: '#F8F9FF' }}
      >
        <div className="sp-list-icon" style={{ background: color.bg, boxShadow: `0 4px 12px ${color.shadow}` }}>
          <i className="fas fa-cube" />
        </div>
        <div className="sp-list-name">
          <h4>{service.name}</h4>
          <span>{service.description || '—'}</span>
        </div>
        <span className={`sp-list-status ${isActive ? 'active' : 'inactive'}`}>
          {isActive ? 'Actif' : 'Inactif'}
        </span>
        <span className="sp-list-pts">{pts.toLocaleString()} pts</span>
        <span className="sp-list-used">{stats?.used_points?.toLocaleString() || 0} utilisés</span>
        <div className="sp-list-actions" ref={menuRef}>
          <button className="sp-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
            <i className="fas fa-ellipsis-h" />
          </button>
          <AnimatePresence>
            {menuOpen && <DropdownMenu service={service} isActive={isActive} onReplenish={onReplenish} onDelete={onDelete} navigate={navigate} close={() => setMenuOpen(false)} />}
          </AnimatePresence>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="sp-card"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -4, boxShadow: '0 12px 40px rgba(37,99,235,0.15)' }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
    >
      <div className="sp-card-top">
        <div className="sp-card-icon" style={{ background: color.bg, boxShadow: `0 6px 16px ${color.shadow}` }}>
          <i className="fas fa-cube" />
        </div>
        <span className="sp-card-pts">{pts.toLocaleString()} pts</span>
        <div className="sp-card-menu-wrap" ref={menuRef}>
          <button className="sp-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
            <i className="fas fa-ellipsis-v" />
          </button>
          <AnimatePresence>
            {menuOpen && <DropdownMenu service={service} isActive={isActive} onReplenish={onReplenish} onDelete={onDelete} navigate={navigate} close={() => setMenuOpen(false)} />}
          </AnimatePresence>
        </div>
      </div>

      <div className="sp-card-info">
        <h4 className="sp-card-name">{service.name}</h4>
        <span className={`sp-card-status ${isActive ? 'active' : 'inactive'}`}>
          <i className={`fas ${isActive ? 'fa-circle' : 'fa-circle'}`} />
          {isActive ? 'Actif' : 'Inactif'}
        </span>
      </div>

      {stats?.points_approvisionnes > 0 && (
        <div className="sp-card-progress">
          <div className="sp-progress-bar">
            <div
              className="sp-progress-fill"
              style={{ width: `${Math.min((stats.used_points / stats.points_approvisionnes) * 100, 100)}%` }}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};

const DropdownMenu = ({ service, isActive, onReplenish, onDelete, navigate, close }) => (
  <motion.div
    className="sp-dropdown"
    initial={{ opacity: 0, scale: 0.95, y: -8 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.95, y: -8 }}
    transition={{ duration: 0.12 }}
  >
    <button onClick={() => { onReplenish(service); close(); }}>
      <i className="fas fa-cart-plus" /> Approvisionner
    </button>
    <button onClick={() => { navigate(`/dashboard/partner/b2c-packs?service=${service.id}`); close(); }} disabled={!isActive}>
      <i className="fas fa-boxes" /> Gérer les packs
    </button>
    <button onClick={() => { navigate(`/dashboard/partner/sales?service=${service.id}`); close(); }}>
      <i className="fas fa-chart-bar" /> Statistiques
    </button>
    <div className="sp-dropdown-divider" />
    <button className="danger" onClick={() => {
      if (window.confirm(`Supprimer "${service.name}" ?`)) onDelete(service.id);
      close();
    }}>
      <i className="fas fa-trash-alt" /> Supprimer
    </button>
  </motion.div>
);

// ── Modal Approvisionner ──────────────────────────────────────
const ApprovModal = ({ isOpen, onClose, onApprovisionner, service, walletPoints, loading }) => {
  const [points, setPoints] = useState(5000);
  const [error, setError] = useState('');
  if (!isOpen) return null;

  return (
    <motion.div className="sp-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="sp-modal" initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }} onClick={e => e.stopPropagation()}>
        <div className="sp-modal-header">
          <div className="sp-modal-icon"><i className="fas fa-coins" /></div>
          <div>
            <h3>Approvisionner le service</h3>
            <p>{service?.name}</p>
          </div>
          <button className="sp-modal-close" onClick={onClose}><i className="fas fa-times" /></button>
        </div>

        <div className="sp-modal-wallet">
          <i className="fas fa-wallet" />
          <span>Wallet disponible</span>
          <strong>{walletPoints?.toLocaleString() || 0} pts</strong>
        </div>

        <div className="sp-modal-field">
          <label>Points à approvisionner</label>
          <div className="sp-modal-input">
            <input
              type="number" min="5000" max={walletPoints} value={points}
              onChange={e => { setPoints(parseInt(e.target.value) || 0); setError(''); }}
            />
            <span>pts</span>
          </div>
          {error && <span className="sp-modal-error">{error}</span>}
          <small>Minimum : <strong>5 000 points</strong></small>
        </div>

        <div className="sp-modal-footer">
          <button className="sp-btn-cancel" onClick={onClose}>Annuler</button>
          <button
            className="sp-btn-confirm"
            onClick={() => {
              if (points < 5000) return setError('Minimum 5000 points');
              if (points > walletPoints) return setError('Points insuffisants');
              onApprovisionner(points);
            }}
            disabled={loading}
          >
            {loading ? <><i className="fas fa-spinner fa-spin" /> En cours…</> : <><i className="fas fa-check" /> Approvisionner {points.toLocaleString()} pts</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Modal Création service ────────────────────────────────────
const CreateModal = ({ isOpen, onClose, onCreate, loading }) => {
  const [form, setForm] = useState({ name: '', description: '', default_currency: 'TND' });
  if (!isOpen) return null;

  return (
    <motion.div className="sp-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div className="sp-modal" initial={{ scale: 0.9, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 30 }} onClick={e => e.stopPropagation()}>
        <div className="sp-modal-header">
          <div className="sp-modal-icon" style={{ background: 'linear-gradient(135deg,#2563eb,#0891b2)' }}><i className="fas fa-plus" /></div>
          <div>
            <h3>Nouveau service</h3>
            <p>Créer et configurer un service</p>
          </div>
          <button className="sp-modal-close" onClick={onClose}><i className="fas fa-times" /></button>
        </div>

        <div className="sp-modal-field">
          <label>Nom du service <span>*</span></label>
          <input className="sp-modal-text-input" placeholder="Ex: AI-Vision Edit…" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="sp-modal-field">
          <label>Description</label>
          <textarea className="sp-modal-text-input" rows="3" placeholder="Décrivez votre service…" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>
        <div className="sp-modal-field">
          <label>Devise</label>
          <select className="sp-modal-text-input" value={form.default_currency} onChange={e => setForm({ ...form, default_currency: e.target.value })}>
            <option value="TND">Dinar tunisien (DT)</option>
            <option value="EUR">Euro (€)</option>
            <option value="USD">Dollar ($)</option>
          </select>
        </div>

        <div className="sp-modal-footer">
          <button className="sp-btn-cancel" onClick={onClose}>Annuler</button>
          <button className="sp-btn-confirm" onClick={() => onCreate(form)} disabled={loading || !form.name.trim()}>
            {loading ? <><i className="fas fa-spinner fa-spin" /> Création…</> : <><i className="fas fa-plus" /> Créer le service</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ── Page principale ───────────────────────────────────────────
const ServicesPartner = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [servicesStats, setServicesStats] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApprovModal, setShowApprovModal] = useState(false);
  const [pendingService, setPendingService] = useState(null);
  const [approvisionnant, setApprovisionnant] = useState(false);
  const [creatingService, setCreatingService] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { fetchServices(); fetchWallet(); }, []);

  const fetchWallet = async () => {
    try {
      const res = await API.get('/wallet/my-wallet');
      setWallet(res.data.wallet);
    } catch {}
  };

  const fetchServices = async () => {
    try {
      const res = await API.get('/companies/my');
      const servicesData = res.data.companies || [];
      setServices(servicesData);

      const approvRes = await API.get('/wallet/approvisionnements');
      const approvMap = {};
      approvRes.data.approvisionnements?.forEach(a => { approvMap[a.company_id] = a; });

      const stats = {};
      servicesData.forEach(s => {
        const a = approvMap[s.id];
        stats[s.id] = {
          points: a?.remaining_points || 0,
          points_approvisionnes: a?.points_approvisionnes || 0,
          used_points: a?.used_points || 0,
        };
      });
      setServicesStats(stats);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateService = async (form) => {
    if (!wallet || wallet.available_points < 5000) {
      setMsg({ type: 'error', text: 'Points insuffisants (minimum 5 000 requis)' });
      return;
    }
    setCreatingService(true);
    try {
      const res = await API.post('/companies/create', form);
      const service = res.data.company;
      setShowCreateModal(false);
      setPendingService(service);
      setShowApprovModal(true);
      await fetchServices();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Erreur création' });
    } finally {
      setCreatingService(false);
    }
  };

  const handleApprovisionner = async (points) => {
    if (!pendingService) return;
    setApprovisionnant(true);
    try {
      const res = await API.post('/wallet/approvisionner', { company_id: pendingService.id, points });
      setMsg({ type: 'success', text: res.data.message });
      setShowApprovModal(false);
      setPendingService(null);
      await fetchServices();
      await fetchWallet();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || "Erreur d'approvisionnement" });
    } finally {
      setApprovisionnant(false);
    }
  };

  const handleReplenish = (service) => {
    if (!wallet || wallet.available_points < 5000) {
      setMsg({ type: 'warning', text: 'Points insuffisants (minimum 5 000 requis)' });
      return;
    }
    setPendingService(service);
    setShowApprovModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/companies/${id}`);
      setMsg({ type: 'success', text: 'Service supprimé' });
      await fetchServices();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Erreur suppression' });
    }
  };

  const filtered = services.filter(s => {
    const isActive = (servicesStats[s.id]?.points || 0) >= 5000;
    const matchCat = activeCategory === 'all' || (activeCategory === 'active' && isActive) || (activeCategory === 'inactive' && !isActive);
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="sp-page">

      {/* ── Hero Banner ─────────────────────────────────────────── */}
      <motion.div
        className="sp-hero"
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 220, damping: 22 }}
      >
        <div className="sp-hero-orb sp-hero-orb-1" />
        <div className="sp-hero-orb sp-hero-orb-2" />
        <div className="sp-hero-left">
          <div className="sp-hero-icon"><i className="fas fa-cube" /></div>
          <div>
            <h1>Mes Services</h1>
            <p>Gérez et approvisionnez vos services</p>
          </div>
        </div>
        <div className="sp-hero-pill" style={{ zIndex: 1 }}>
          <i className="fas fa-wallet" />
          <span>{wallet?.available_points?.toLocaleString('fr-FR') || '0'} pts disponibles</span>
        </div>
      </motion.div>

      <div className="sp-panel">

        {/* ── Sidebar ─────────────────────────────────────────── */}
        <aside className="sp-sidebar">
          <div className="sp-sidebar-brand">
            <div className="sp-brand-icon"><i className="fas fa-cube" /></div>
            <span>Services</span>
          </div>

          <nav className="sp-sidebar-nav">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`sp-nav-item ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                <i className={cat.icon} />
                <span>{cat.label}</span>
              </button>
            ))}
          </nav>

          <div className="sp-sidebar-wallet">
            <div className="sp-wallet-circle">
              <i className="fas fa-wallet" />
            </div>
            <div>
              <span>Wallet</span>
              <strong>{wallet?.available_points?.toLocaleString() || 0} pts</strong>
            </div>
          </div>
        </aside>

        {/* ── Main ────────────────────────────────────────────── */}
        <main className="sp-main">

          {/* Top bar */}
          <div className="sp-topbar">
            <div className="sp-search">
              <i className="fas fa-search" />
              <input
                placeholder="Rechercher un service…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <button className="sp-btn-upload" onClick={() => setShowCreateModal(true)}>
              <i className="fas fa-plus" />
              Nouveau service
            </button>
          </div>

          {/* Alert */}
          <AnimatePresence>
            {msg && (
              <motion.div
                className={`sp-alert sp-alert--${msg.type}`}
                initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              >
                <i className={`fas ${msg.type === 'success' ? 'fa-check-circle' : msg.type === 'warning' ? 'fa-exclamation-triangle' : 'fa-times-circle'}`} />
                {msg.text}
                <button onClick={() => setMsg(null)}><i className="fas fa-times" /></button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Content header */}
          <div className="sp-content-header">
            <div>
              <h2>
                {activeCategory === 'all' ? 'Tous les services' :
                  activeCategory === 'active' ? 'Services actifs' : 'Services inactifs'}
              </h2>
              <p>Total : {filtered.length} service{filtered.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="sp-controls">
              <div className="sp-view-toggle">
                <button className={viewMode === 'list' ? 'active' : ''} onClick={() => setViewMode('list')} title="Vue liste">
                  <i className="fas fa-list" />
                </button>
                <button className={viewMode === 'grid' ? 'active' : ''} onClick={() => setViewMode('grid')} title="Vue grille">
                  <i className="fas fa-th" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className="sp-loader">
              <div className="sp-spinner" />
              <p>Chargement…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="sp-empty">
              <div className="sp-empty-icon"><i className="fas fa-cube" /></div>
              <h3>{search ? 'Aucun résultat' : 'Aucun service'}</h3>
              <p>{search ? `Aucun service ne correspond à "${search}"` : 'Créez votre premier service'}</p>
              {!search && (
                <button className="sp-btn-upload" onClick={() => setShowCreateModal(true)}>
                  <i className="fas fa-plus" /> Nouveau service
                </button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="sp-grid">
              {filtered.map(service => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  stats={servicesStats[service.id]}
                  onReplenish={handleReplenish}
                  onDelete={handleDelete}
                  navigate={navigate}
                  viewMode="grid"
                />
              ))}
            </div>
          ) : (
            <div className="sp-list">
              <div className="sp-list-header">
                <span>Nom</span>
                <span>Statut</span>
                <span>Points restants</span>
                <span>Points utilisés</span>
                <span></span>
              </div>
              {filtered.map(service => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  stats={servicesStats[service.id]}
                  onReplenish={handleReplenish}
                  onDelete={handleDelete}
                  navigate={navigate}
                  viewMode="list"
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreateService}
            loading={creatingService}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showApprovModal && (
          <ApprovModal
            isOpen={showApprovModal}
            onClose={() => { setShowApprovModal(false); setPendingService(null); }}
            onApprovisionner={handleApprovisionner}
            service={pendingService}
            walletPoints={wallet?.available_points}
            loading={approvisionnant}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServicesPartner;
